import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, IndianRupee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card_pos', label: 'Card (POS)' },
  { value: 'upi_qr', label: 'UPI QR' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'internal_adj', label: 'Internal Adjustment' },
];

const emptyPassenger = () => ({
  ti: 'Mr', fN: '', lN: '', pt: 'ADULT', dob: '', nationality: 'IN',
});

export default function StaffReviewBooking() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [margin, setMargin] = useState(null);
  const [passengers, setPassengers] = useState([emptyPassenger()]);
  const [contact, setContact] = useState({ name: '', email: '', phone: '', gst_name: '', gst_number: '' });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('staff_booking_draft');
      if (!raw) {
        navigate('/staff/search');
        return;
      }
      const parsed = JSON.parse(raw);
      setDraft(parsed);
    } catch {
      navigate('/staff/search');
    }
  }, [navigate]);

  useEffect(() => {
    if (!draft?.flightData) return;
    const fd = draft.flightData;
    setQuoteLoading(true);
    axios
      .post(
        `${BACKEND_URL}/api/staff/booking/quote`,
        {
          source: fd.source || 'tripjack',
          priceId: fd.priceId || fd.price_id,
          priceIds: fd.priceId ? [fd.priceId] : undefined,
          flightData: fd,
        },
        { withCredentials: true },
      )
      .then((res) => setMargin(res.data))
      .catch(() => {})
      .finally(() => setQuoteLoading(false));
  }, [draft]);

  const updatePassenger = (idx, field, value) => {
    setPassengers((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!contact.name.trim()) {
      setError('Walk-in customer name is required');
      return;
    }
    setLoading(true);
    setError('');
    const fd = draft.flightData;
    const source = (fd.source || 'tripjack').toLowerCase();
    const sellPrice = Number(fd.price || margin?.sell_price || 0);

    const payload = {
      source,
      session_id: crypto.randomUUID?.() || String(Date.now()),
      flightData: fd,
      total_amount: sellPrice,
      walkin_contact: contact,
      payment_method: paymentMethod,
      payment_reference: paymentRef || undefined,
      passengers,
    };

    if (source === 'tripjack') {
      payload.priceIds = [fd.priceId || fd.price_id].filter(Boolean);
      payload.travellerInfo = passengers;
      payload.deliveryInfo = {
        emails: contact.email ? [contact.email] : [],
        contacts: contact.phone ? [contact.phone] : [],
      };
      payload.paymentInfos = [{ amount: sellPrice }];
      if (margin?.review) payload.review_snapshot = margin.review;
    }

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/staff/booking/checkout`,
        payload,
        { withCredentials: true },
      );
      sessionStorage.removeItem('staff_booking_draft');
      navigate('/staff/bookings', {
        state: { success: true, bookingId: res.data.booking_id || res.data.booking?.booking_id },
      });
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  if (!draft) return null;
  const fd = draft.flightData;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="staff-review">
      <h1 className="text-2xl font-bold text-[#1B375B] mb-2">Review & Checkout</h1>
      <p className="text-sm text-gray-500 mb-6">
        {fd.from} → {fd.to} · {fd.airline || fd.airline_code} · ₹{Number(fd.price || 0).toLocaleString('en-IN')}
      </p>

      {margin && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><p className="text-[10px] uppercase text-gray-500">Supplier net</p><p className="font-bold">₹{margin.supplier_net?.toLocaleString('en-IN')}</p></div>
          <div><p className="text-[10px] uppercase text-gray-500">Markup</p><p className="font-bold">₹{margin.markup?.toLocaleString('en-IN')}</p></div>
          <div><p className="text-[10px] uppercase text-gray-500">Sell price</p><p className="font-bold">₹{margin.sell_price?.toLocaleString('en-IN')}</p></div>
          <div><p className="text-[10px] uppercase text-gray-500">Margin</p><p className="font-bold text-emerald-800 flex items-center gap-1"><IndianRupee className="w-3 h-3" />{margin.margin?.toLocaleString('en-IN')} ({margin.margin_pct}%)</p></div>
        </div>
      )}
      {quoteLoading && <p className="text-xs text-gray-400 mb-4">Loading margin…</p>}

      <form onSubmit={handleCheckout} className="space-y-6">
        <section className="bg-white rounded-xl border p-4">
          <h2 className="font-bold text-[#1B375B] mb-3">Passengers</h2>
          {passengers.map((p, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 mb-3">
              <input placeholder="First name" value={p.fN} onChange={(e) => updatePassenger(i, 'fN', e.target.value)} className="px-3 py-2 border rounded-lg text-sm" required />
              <input placeholder="Last name" value={p.lN} onChange={(e) => updatePassenger(i, 'lN', e.target.value)} className="px-3 py-2 border rounded-lg text-sm" required />
            </div>
          ))}
        </section>

        <section className="bg-white rounded-xl border p-4">
          <h2 className="font-bold text-[#1B375B] mb-3">Walk-in contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Name *" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" required data-testid="walkin-name" />
            <input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="Email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="GST name" value={contact.gst_name} onChange={(e) => setContact({ ...contact, gst_name: e.target.value })} className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder="GST number" value={contact.gst_number} onChange={(e) => setContact({ ...contact, gst_number: e.target.value })} className="px-3 py-2 border rounded-lg text-sm md:col-span-2" />
          </div>
        </section>

        <section className="bg-white rounded-xl border p-4">
          <h2 className="font-bold text-[#1B375B] mb-3">Payment</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="payment-method">
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <input placeholder="Receipt / UTRN reference" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" />
          </div>
        </section>

        {error && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex gap-2 text-sm text-rose-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[#1B375B] text-white rounded-xl font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="staff-checkout-submit"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
          Complete booking
        </button>
      </form>
    </div>
  );
}
